// TODO: replace with paymentBatchService — this whole file is mock data used while
// the payment-batch backend API is being built. Interfaces here will move into a
// service module once the real API contract is finalized.

export type BatchInvoiceStatus = 'Paid' | 'Pending' | 'Draft' | 'Overdue'
export type ProviderPayoutStatus = 'Pending' | 'Ready' | 'Paid' | 'Processing'
export type StripeStatus = 'Connected' | 'Not Connected'
export type PaymentBatchStatus =
  | 'Draft'
  | 'Pending'
  | 'Ready for Payment'
  | 'Processing'
  | 'Paid'

export interface BatchProviderRef {
  _id: string
  name: string
  role: string // e.g. PT / OT / ST
  avatar?: string
}

export interface BatchInvoice {
  _id: string
  invoice_ids: string
  contract_ids: string
  session_ids: string
  patient_name: string
  service_name: string
  totalAmount: number
  providerPayout: number
  payment_date?: string
  payment_method: string
  payment_for: string
  claim_id?: string
  status: BatchInvoiceStatus
  provider: BatchProviderRef
}

export interface ProviderInvoiceLine {
  _id?: string
  invoice_ids: string
  amount: number
}

export interface ProviderPayout {
  providerId: string
  name: string
  role: string
  avatar?: string
  invoiceCount: number
  sessions: number
  payableAmount: number
  paymentMethod: string
  status: ProviderPayoutStatus
  stripeStatus: StripeStatus
  invoices: ProviderInvoiceLine[]
}

export interface PaymentTimelineEntry {
  label: string
  timestamp: string
}

export interface PaymentBatch {
  _id: string
  name: string
  month: string
  description?: string
  createdBy: string
  createdDate: string
  paymentDate?: string
  status: PaymentBatchStatus
  totalProviders: number
  totalInvoices: number
  totalSessions: number
  totalAmount: number
  totalPayout: number
  paymentMethod: string
  providers: ProviderPayout[]
  timeline: PaymentTimelineEntry[]
  batchType?: 'invoice' | 'claim'
}

// ---------------------------------------------------------------------------
// Mock invoices (provider-centric) shown on the redesigned Invoices page.
// ---------------------------------------------------------------------------
export const MOCK_BATCH_INVOICES: BatchInvoice[] = [
  {
    _id: 'inv-1',
    invoice_ids: 'INV-001',
    contract_ids: 'CN-1001',
    session_ids: 'S001',
    patient_name: 'Sniffet Color',
    service_name: 'Physical Therapy',
    totalAmount: 1200,
    providerPayout: 1000,
    payment_date: '2025-09-21',
    payment_method: 'Card',
    payment_for: 'Out-of-Pocket',
    status: 'Paid',
    provider: { _id: 'prov-1', name: 'Dr. Sarah Chen', role: 'PT' },
  },
  {
    _id: 'inv-2',
    invoice_ids: 'INV-002',
    contract_ids: 'CN-1002',
    session_ids: 'S002',
    patient_name: 'Andy Ros',
    service_name: 'Occupational Therapy',
    totalAmount: 800,
    providerPayout: 0,
    payment_method: 'Card',
    payment_for: 'Out-of-Pocket',
    status: 'Draft',
    provider: { _id: 'prov-2', name: 'Michael Brown', role: 'OT' },
  },
  {
    _id: 'inv-3',
    invoice_ids: 'INV-003',
    contract_ids: 'CN-1003',
    session_ids: 'S003',
    patient_name: 'Garvet Luck',
    service_name: 'Physical Therapy',
    totalAmount: 800,
    providerPayout: 600,
    payment_date: '2025-09-25',
    payment_method: 'Card',
    payment_for: 'Card',
    status: 'Paid',
    provider: { _id: 'prov-3', name: 'Lisa Anderson', role: 'ST' },
  },
  {
    _id: 'inv-4',
    invoice_ids: 'INV-003',
    contract_ids: 'CN-1003',
    session_ids: 'S003',
    patient_name: 'Garvet Luck',
    service_name: 'Physical Therapy',
    totalAmount: 800,
    providerPayout: 600,
    payment_date: '2025-09-25',
    payment_method: 'Card',
    payment_for: 'Co-pay',
    claim_id: 'clm12345',
    status: 'Paid',
    provider: { _id: 'prov-4', name: 'David Kim', role: 'ST' },
  },
  {
    _id: 'inv-5',
    invoice_ids: 'INV-003',
    contract_ids: 'CN-1003',
    session_ids: 'S003',
    patient_name: 'Garvet Luck',
    service_name: 'Physical Therapy',
    totalAmount: 800,
    providerPayout: 600,
    payment_date: '2025-09-25',
    payment_method: 'Card',
    payment_for: 'Co-pay',
    claim_id: 'clm12345',
    status: 'Paid',
    provider: { _id: 'prov-4', name: 'David Kim', role: 'ST' },
  },
  {
    _id: 'inv-6',
    invoice_ids: 'INV-003',
    contract_ids: 'CN-1003',
    session_ids: 'S003',
    patient_name: 'Garvet Luck',
    service_name: 'Physical Therapy',
    totalAmount: 800,
    providerPayout: 600,
    payment_date: '2025-09-25',
    payment_method: 'Card',
    payment_for: 'Co-pay',
    claim_id: 'clm12345',
    status: 'Paid',
    provider: { _id: 'prov-4', name: 'David Kim', role: 'ST' },
  },
]

// ---------------------------------------------------------------------------
// Mock payment batches for the Payment Batches list + detail pages.
// ---------------------------------------------------------------------------
const SAMPLE_PROVIDERS: ProviderPayout[] = [
  {
    providerId: 'prov-1',
    name: 'Dr. Sarah Chen',
    role: 'PT',
    invoiceCount: 5,
    sessions: 12,
    payableAmount: 1250,
    paymentMethod: 'Stripe Connect',
    status: 'Pending',
    stripeStatus: 'Connected',
    invoices: [
      { invoice_ids: 'INV-001', amount: 300 },
      { invoice_ids: 'INV-002', amount: 250 },
      { invoice_ids: 'INV-003', amount: 280 },
      { invoice_ids: 'INV-004', amount: 220 },
      { invoice_ids: 'INV-005', amount: 200 },
    ],
  },
  {
    providerId: 'prov-2',
    name: 'Michael Brown',
    role: 'OT',
    invoiceCount: 3,
    sessions: 8,
    payableAmount: 980,
    paymentMethod: 'Stripe Connect',
    status: 'Pending',
    stripeStatus: 'Connected',
    invoices: [
      { invoice_ids: 'INV-011', amount: 400 },
      { invoice_ids: 'INV-012', amount: 300 },
      { invoice_ids: 'INV-013', amount: 280 },
    ],
  },
  {
    providerId: 'prov-3',
    name: 'Lisa Anderson',
    role: 'ST',
    invoiceCount: 4,
    sessions: 10,
    payableAmount: 860,
    paymentMethod: 'Stripe Connect',
    status: 'Pending',
    stripeStatus: 'Connected',
    invoices: [
      { invoice_ids: 'INV-021', amount: 260 },
      { invoice_ids: 'INV-022', amount: 200 },
      { invoice_ids: 'INV-023', amount: 200 },
      { invoice_ids: 'INV-024', amount: 200 },
    ],
  },
  {
    providerId: 'prov-4',
    name: 'David Kim',
    role: 'ST',
    invoiceCount: 4,
    sessions: 11,
    payableAmount: 920,
    paymentMethod: 'Stripe Connect',
    status: 'Pending',
    stripeStatus: 'Connected',
    invoices: [
      { invoice_ids: 'INV-031', amount: 260 },
      { invoice_ids: 'INV-032', amount: 240 },
      { invoice_ids: 'INV-033', amount: 220 },
      { invoice_ids: 'INV-034', amount: 200 },
    ],
  },
]

const makeBatch = (
  id: string,
  status: PaymentBatchStatus
): PaymentBatch => ({
  _id: id,
  name: 'Provider Payroll – May 2026',
  month: 'May 2026',
  description: '',
  createdBy: 'CareFirst Admin',
  createdDate: '2026-05-29',
  paymentDate: '2025-09-21',
  status,
  totalProviders: 12,
  totalInvoices: 38,
  totalSessions: 0,
  totalAmount: 8250,
  totalPayout: 8250,
  paymentMethod: 'Stripe Connect',
  providers: SAMPLE_PROVIDERS,
  timeline: [{ label: 'Created', timestamp: '2026-05-29T09:12:00' }],
})

export const MOCK_PAYMENT_BATCHES: PaymentBatch[] = [
  makeBatch('batch-1', 'Ready for Payment'),
  makeBatch('batch-2', 'Paid'),
  makeBatch('batch-3', 'Paid'),
  makeBatch('batch-4', 'Draft'),
]

export const getMockBatchById = (id: string): PaymentBatch | undefined =>
  MOCK_PAYMENT_BATCHES.find(batch => batch._id === id)

export const getInitials = (name: string): string =>
  name
    .replace(/^dr\.?\s+/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('')

export const formatCurrency = (value: number): string =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
